// src/pages/Resources/Resources.tsx

import React, { useState, useEffect, useRef } from 'react';
import {
  NavTabs,
  NavTab,
  SectionWrapper,
  ContentContainer,
  SectionTitle,
  BenefitsList,
} from './Resources.styles';
import { FiCheckCircle, FiClock } from 'react-icons/fi';
import { PageContainer } from '../../components/common/PageContainer/PageContainer.styles';
import { 
  CircularProgress, 
  Box, 
  Typography, 
  Grid,
  Chip,
  Divider,
  useTheme,
  alpha,
  Container,
  Paper
} from '@mui/material';
import { resourceService } from '../../services/api/resourcesService';
import StarIcon from '@mui/icons-material/Star';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LabelIcon from '@mui/icons-material/Label';

const Resources: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<string>('#palliative-care');
  const [dynamicSections, setDynamicSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const videoRefs = useRef<{[key: string]: HTMLVideoElement | null}>({});

  // Handle tab clicks and smooth scrolling
  const handleNavTabClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    setActiveTab(sectionId);
    const targetSection = document.querySelector(sectionId);
    if (targetSection) {
      const yOffset = -140; // offset for a fixed nav
      const yPosition =
        targetSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: yPosition,
        behavior: 'smooth',
      });
    }
  };

  // Update active tab on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['#palliative-care', '#dynamic-sections'];
      for (const section of sections) {
        const el = document.querySelector(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveTab(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch dynamic sections from the API
  useEffect(() => {
    const loadSections = async () => {
      try {
        // Check if API endpoint is configured
        if (!import.meta.env.VITE_API_ENDPOINT) {
          setDynamicSections([]);
          setLoading(false);
          return;
        }

        const data = await resourceService.getResources();
        // Sort sections so featured ones are at the top
        const sortedData = Array.isArray(data) 
          ? [...data].sort((a, b) => (b.featured === true ? 1 : 0) - (a.featured === true ? 1 : 0))
          : [];
        setDynamicSections(sortedData);
      } catch (error) {
        setDynamicSections([]);
      } finally {
        setLoading(false);
      }
    };
    loadSections();
  }, []);

  // Render a video component if a video URL is provided
  const renderVideo = (videoUrl: string, subsectionId: string) => {
    // Check if URL is valid
    if (!videoUrl) return null;
    
    try {
      // Handle different video sources (YouTube, Vimeo, etc.)
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        // Extract video ID
        let videoId = '';
        if (videoUrl.includes('watch?v=')) {
          videoId = new URL(videoUrl).searchParams.get('v') || '';
        } else if (videoUrl.includes('youtu.be/')) {
          videoId = videoUrl.split('youtu.be/')[1];
        }
        
        if (!videoId) return null;
        
        return (
          <Box
            component="iframe"
            src={`https://www.youtube.com/embed/${videoId}`}
            width="100%"
            height="100%"
            sx={{ 
              borderRadius: 2, 
              border: 'none',
              aspectRatio: '16/9',
              minHeight: '280px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      } else if (videoUrl.includes('vimeo.com')) {
        // For Vimeo videos
        const vimeoId = videoUrl.split('/').pop() || '';
        return (
          <Box
            component="iframe"
            src={`https://player.vimeo.com/video/${vimeoId}`}
            width="100%"
            height="100%"
            sx={{ 
              borderRadius: 2, 
              border: 'none',
              aspectRatio: '16/9',
              minHeight: '280px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        );
      }
      
      // For direct video URLs (S3, etc.)
      return (
        <Box
          component="video"
          ref={(el) => {
            videoRefs.current[subsectionId] = el as HTMLVideoElement;
          }}
          controls
          preload="metadata"
          width="100%"
          height="100%"
          sx={{ 
            borderRadius: 2, 
            aspectRatio: '16/9',
            minHeight: '280px',
            objectFit: 'cover',
            backgroundColor: '#000',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
          src={videoUrl}
          onError={(e) => {
            console.error('Video error:', e);
          }}
        />
      );
    } catch (e) {
      console.error('Error rendering video:', e);
      return (
        <Box 
          sx={{ 
            width: '100%',
            height: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            padding: 2
          }}
        >
          <VideoLibraryIcon sx={{ fontSize: 60, color: '#999', mb: 2 }} />
          <Typography color="text.secondary">Video could not be loaded</Typography>
        </Box>
      );
    }
  };

  // Get background style based on section configuration
  const getSectionBackground = (section: any) => {
    if (!section.backgroundStyle || section.backgroundStyle === 'light') {
      return '#ffffff';
    } else if (section.backgroundStyle === 'dark') {
      return '#f5f7fa';
    } else if (section.backgroundStyle === 'gradient') {
      return `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`;
    } else if (section.backgroundStyle === 'image' && section.coverImageUrl) {
      return `url(${section.coverImageUrl}) center/cover no-repeat`;
    }
    return '#ffffff';
  };

  // Render image with proper handling for errors
  const renderImage = (imageUrl: string, altText: string = 'Resource image') => {
    if (!imageUrl) return null;
    
    return (
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          height: '100%', 
          minHeight: '280px',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={altText}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.5s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            }
          }}
          onError={(e) => {
            // Set fallback image on error
            const target = e.target as HTMLImageElement;
            target.src = 'https://via.placeholder.com/400x300?text=Image+Unavailable';
          }}
        />
      </Box>
    );
  };

  // Format bulleted list with icons and styling
  const renderBulletPoints = (bulletPoints: string[], accentColor: string) => {
    if (!bulletPoints || bulletPoints.length === 0) return null;
    
    return (
      <BenefitsList>
        {bulletPoints.map((point: string, idx: number) => (
          <li key={idx} style={{ marginBottom: '10px' }}>
            <FiCheckCircle 
              style={{ 
                marginRight: 10, 
                color: accentColor,
                fontSize: '1.2rem',
                flexShrink: 0
              }} 
            />
            <Typography variant="body1" style={{ lineHeight: 1.5 }}>
              {point}
            </Typography>
          </li>
        ))}
      </BenefitsList>
    );
  };

  return (
    <PageContainer>

      {/* Navigation Tabs */}
      <NavTabs>
        <NavTab
          href="#palliative-care"
          onClick={(e) => handleNavTabClick(e, '#palliative-care')}
          $active={activeTab === '#palliative-care'}
        >
          Palliative Care Overview
        </NavTab>
        <NavTab
          href="#dynamic-sections"
          onClick={(e) => handleNavTabClick(e, '#dynamic-sections')}
          $active={activeTab === '#dynamic-sections'}
        >
          More Resources
        </NavTab>
      </NavTabs>

      {/* Static Section: Palliative Care Overview */}
      <SectionWrapper id="palliative-care">
        <ContentContainer>
          <SectionTitle style={{ fontSize: '1.75rem', marginBottom: '1.5rem' }}>
            Understanding Palliative Care: A Guide to Comprehensive Care
          </SectionTitle>
          
          <Typography variant="body1" paragraph>
            Palliative care is a specialized healthcare approach designed to improve quality of life for patients with serious illnesses. This comprehensive support system addresses physical, emotional, and spiritual needs, ensuring patients receive the care and dignity they deserve throughout their healthcare journey.
          </Typography>

          <Typography variant="h3" align="center" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Holistic Patient-Centered Support
          </Typography>
          <Typography variant="body1" paragraph>
            At its core, palliative care looks beyond medical diagnoses to see the whole person. This approach:
          </Typography>
          <ul>
            <li>Provides personalized care plans that address each patient's unique needs and circumstances</li>
            <li>Focuses on effective symptom management and stress reduction</li>
            <li>Prioritizes patient dignity and personal preferences in all care decisions</li>
            <li>Extends support to family members and caregivers, recognizing their vital role in the care journey</li>
            <li>Creates a supportive environment where patients feel heard and respected</li>
          </ul>

          <Typography variant="h3" align="center" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Multidisciplinary Care Strategy
          </Typography>
          <Typography variant="body1" paragraph>
            Palliative care brings together diverse healthcare professionals who work as a unified team to provide comprehensive support. This collaborative approach includes:
          </Typography>
          <ul>
            <li>Physicians who specialize in pain management and symptom control</li>
            <li>Nurses who provide direct patient care and coordinate treatment plans</li>
            <li>Social workers who help navigate healthcare systems and access resources</li>
            <li>Chaplains who offer spiritual support and guidance</li>
            <li>Additional specialists based on individual patient needs</li>
            <li>Regular team meetings to ensure coordinated, effective care delivery</li>
          </ul>

          <Typography variant="h3" align="center" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Comprehensive Symptom and Life Management
          </Typography>
          <Typography variant="body1" paragraph>
            The palliative care approach addresses multiple aspects of living with serious illness:
          </Typography>
          <ul>
            <li>Expert management of physical symptoms such as pain, nausea/vomiting, and breathing difficulties</li>
            <li>Emotional support to help cope with anxiety, depression, and stress</li>
            <li>Assistance with advance care planning to ensure future care aligns with patient wishes</li>
            <li>Support in identifying and pursuing meaningful life goals despite illness limitations</li>
            <li>Regular assessment and adjustment of care plans to meet changing needs</li>
            <li>Education and resources for patients and families to make informed decisions</li>
          </ul>
        </ContentContainer>
      </SectionWrapper>

      {/* Dynamic Sections Container */}
      {loading ? (
        <SectionWrapper id="dynamic-sections" background="#fff">
          <ContentContainer>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
              <CircularProgress />
            </Box>
          </ContentContainer>
        </SectionWrapper>
      ) : dynamicSections.length > 0 ? (
        <div id="dynamic-sections">
          {dynamicSections.map((section: any, index: number) => {
            // Each section is expected to have:
            //   mainTitle, mainDescription, subsections (array)
            const { id, mainTitle, mainDescription, subsections, tags, coverImageUrl, coverImageAlt } = section;
            
            const sectionBackground = getSectionBackground(section);
            const isFeatured = section.featured === true;
            

            return (
              <SectionWrapper 
                key={id || index} 
                style={{ 
                  background: sectionBackground,
                  position: 'relative',
                  padding: '5rem 2rem',
                  marginBottom: '2rem',
                  boxShadow: isFeatured ? '0 4px 20px rgba(0,0,0,0.1)' : '0 2px 10px rgba(0,0,0,0.05)',
                  borderRadius: isFeatured ? '12px' : '8px'
                }}
              >
                <Container maxWidth="lg">
                  {/* Featured badge if applicable */}
                  {isFeatured && (
                    <Box 
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: '2rem',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        py: 0.5,
                        px: 2,
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        fontWeight: 'bold',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <StarIcon fontSize="small" />
                      Featured Resource
                    </Box>
                  )}

                  {/* Tags */}
                  {tags && tags.length > 0 && (
                    <Box mb={3} display="flex" flexWrap="wrap" gap={1} justifyContent="center">
                      {tags.map((tag: string, tagIdx: number) => (
                        <Chip 
                          key={tagIdx}
                          icon={<LabelIcon />}
                          label={tag}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Cover Image */}
                  {coverImageUrl && (
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                      <Box
                        component="img" 
                        src={coverImageUrl} 
                        alt={coverImageAlt || mainTitle} 
                        sx={{ 
                          maxWidth: '100%', 
                          height: 'auto', 
                          borderRadius: '12px',
                          maxHeight: '500px',
                          objectFit: 'cover',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          width: '100%'
                        }} 
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://via.placeholder.com/800x400?text=Resource+Image';
                        }}
                      />
                    </Box>
                  )}

                  {/* Main Title */}
                  <SectionTitle style={{ 
                    textAlign: "center", 
                    marginBottom: '1.5rem', 
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    color: theme.palette.primary.main
                  }}>
                    {mainTitle}
                  </SectionTitle>

                  {/* Main Description */}
                  {mainDescription && (
                    <Typography
                      variant="body1"
                      sx={{ 
                        textAlign: "center", 
                        mb: 5, 
                        maxWidth: '850px',
                        mx: 'auto',
                        fontSize: '1.15rem',
                        lineHeight: 1.7,
                        color: section.backgroundStyle === 'dark' ? 'text.primary' : 'inherit',
                        fontWeight: 400
                      }}
                    >
                      {mainDescription}
                    </Typography>
                  )}

                  {/* Divider */}
                  <Divider sx={{ 
                    mb: 6, 
                    width: '100px', 
                    mx: 'auto', 
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }} />

                  {/* Subsections */}
                  {Array.isArray(subsections) && (
                    <Grid container spacing={4}>
                      {subsections.map((sub: any, subIndex: number) => {
                        const hasSubMedia = sub.imageUrl || sub.videoUrl;
                        const accentColor = sub.accentColor || theme.palette.primary.main;
                        const subsectionId = `${id}-sub-${subIndex}`;
                        
                        return (
                          <Grid item xs={12} key={subsectionId} mb={4}>
                            <Paper 
                              elevation={2} 
                              sx={{ 
                                backgroundColor: sub.backgroundColor || '#fff',
                                borderRadius: 3,
                                overflow: 'hidden',
                                transition: 'transform 0.3s, box-shadow 0.3s',
                                '&:hover': {
                                  transform: 'translateY(-5px)',
                                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                                }
                              }}
                            >
                              {/* Top part: Subtitle and Description */}
                              <Box p={4} pb={hasSubMedia && sub.imageUrl ? 2 : 4} sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="h4"
                                  sx={{ 
                                    fontWeight: 700, 
                                    mb: 2.5,
                                    fontSize: '1.5rem',
                                    color: theme.palette.text.primary,
                                    position: 'relative',
                                    paddingBottom: '12px',
                                    display: 'inline-block',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      bottom: 0,
                                      left: '50%',
                                      width: '50px',
                                      height: '4px',
                                      backgroundColor: accentColor,
                                      borderRadius: '4px',
                                      transform: 'translateX(-50%)'
                                    }
                                  }}
                                >
                                  {sub.subtitle}
                                </Typography>
                                
                                <Typography 
                                  variant="body1" 
                                  paragraph
                                  sx={{ 
                                    mb: 3, 
                                    fontSize: '1.05rem',
                                    lineHeight: 1.7,
                                    maxWidth: '850px',
                                    mx: 'auto',
                                    textAlign: 'center'
                                  }}
                                >
                                  {sub.description}
                                </Typography>
                              </Box>

                              {/* Bottom part: Bullets and Media */}
                              {(sub.bulletPoints?.length > 0 || sub.videoUrl || sub.imageUrl) && (
                                <Grid container spacing={3}>
                                  {/* Bullet points column */}
                                  <Grid item xs={12} md={(sub.videoUrl || sub.imageUrl) ? 6 : 12}>
                                    <Box p={4} pt={0}>
                                      {/* Bullet points, if any */}
                                      {renderBulletPoints(sub.bulletPoints, accentColor)}
                                    </Box>
                                  </Grid>

                                  {/* Media column: Either video or image or both */}
                                  {(sub.videoUrl || sub.imageUrl) && (
                                    <Grid item xs={12} md={6} sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                      position: 'relative'
                                    }}>
                                      {/* Video if exists */}
                                      {sub.videoUrl && (
                                        <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
                                          {renderVideo(sub.videoUrl, subsectionId)}
                                        </Box>
                                      )}
                                      
                                      {/* Image if exists and no video */}
                                      {sub.imageUrl && !sub.videoUrl && (
                                        <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
                                          {renderImage(sub.imageUrl, sub.imageAlt || sub.subtitle)}
                                        </Box>
                                      )}
                                      
                                      {/* Image integrated with video in a more elegant way */}
                                      {sub.imageUrl && sub.videoUrl && (
                                        <Box sx={{ 
                                          p: { xs: 2, md: 3 }, 
                                          mt: 2,
                                          width: '100%',
                                          borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                        }}>
                                          <Box 
                                            sx={{
                                              width: '80%',
                                              aspectRatio: '4/3',
                                              borderRadius: 2,
                                              overflow: 'hidden',
                                              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                                              transition: 'transform 0.3s ease',
                                              '&:hover': {
                                                transform: 'scale(1.03)'
                                              }
                                            }}
                                          >
                                            {renderImage(sub.imageUrl, sub.imageAlt || sub.subtitle)}
                                          </Box>
                                        </Box>
                                      )}
                                    </Grid>
                                  )}
                                </Grid>
                              )}
                              
                              {/* Only display full-width image if it exists alone with no video */}
                              {sub.imageUrl && !sub.videoUrl && !sub.bulletPoints?.length && (
                                <Box sx={{ px: 4, pb: 4 }}>
                                  {renderImage(sub.imageUrl, sub.imageAlt || sub.subtitle)}
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                  
                  {/* Timestamp and metadata - only shown if createdAt exists */}
                  {section.createdAt && (
                    <Box mt={5} display="flex" justifyContent="center" alignItems="center" gap={1}>
                      <FiClock style={{ color: theme.palette.text.secondary }} />
                      <Typography variant="body2" color="text.secondary">
                        Last updated: {new Date(section.updatedAt || section.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Container>
              </SectionWrapper>
            );
          })}
        </div>
      ) : (
        <SectionWrapper id="dynamic-sections" background="#fff">
          <ContentContainer>
            <Typography variant="body2" align="center">
              No additional resources available.
            </Typography>
          </ContentContainer>
        </SectionWrapper>
      )}
    </PageContainer>
  );
};

export default Resources;
